import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { Paginator } from 'src/app/models/paginator';
import { ProductService } from 'src/app/models/productService';
import { Trademark } from 'src/app/models/trademark';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import { NiceService } from 'src/app/services/nice.service';
import { ProductServiceService } from 'src/app/services/product-service.service';

type ProductServiceOption = ProductService & { selected: boolean, disabled?: boolean, display?: boolean }

@Component({
  selector: 'app-product-service-select',
  templateUrl: './product-service-select.component.html',
  styleUrls: ['./product-service-select.component.scss']
})
export class ProductServiceSelectComponent {
  private id: string;
  @Input() label?: string;
  @Input() labelOutside: boolean = false;
  @Input() search: boolean = false;
  @Input() multiple: boolean = false;
  @Input() editTmNice: boolean = true;

  @Input() initial?: string = 'all';
  @Input() disabled: boolean = false;
  @Input() disabledValues: string[] = [];
  @Input() emitOnInit: boolean = true;

  @Input() trademarks: Partial<Trademark>[] = [];
  @Input() niceClassification: number[] = [];

  loading: boolean = false;
  productsServices: Paginator<ProductServiceOption> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0
  };

  selected: ProductServiceOption[] = [];

  query?: string;
  queryUpdate: Subject<string | undefined> = new Subject<string | undefined>();

  @Output() change = new EventEmitter<any>();
  @Input() value?: ProductService | ProductService[];
  @Output() valueChange = new EventEmitter<any>();

  show: boolean = false;

  selection?: string;

  subscription: Subscription;

  @ViewChild('trigger') trigger?: CdkMenuTrigger;

  user?: User;

  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private productServiceService: ProductServiceService,
    private translate: TranslateService
  ) {
    this.id = _.uniqueId("SELECT-");
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "LAYOUT.SELECT") {
        (change.data.id !== this.id) && (this.show = false);
      }
    });
    this.queryUpdate
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(async () => {
        this.resetPage();
        await this.retrieveProductsAndServices(true);
      });
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.setInitialState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['trademarks'] && !_.isEqual(changes['trademarks']['currentValue'], changes['trademarks']['previousValue'])) || (changes['niceClassification'] && !_.isEqual(changes['niceClassification']['currentValue'], changes['niceClassification']['previousValue']))) {
      this.resetPage();
      this.setInitialState();
    }
  }

  _isDisabled(option: ProductService) {
    return (option._id && this.disabledValues.includes(option._id)) ? true : false;
  }

  _isSelected(option: ProductService) {
    return (option._id && this.selected.map((t) => t._id).includes(option._id)) ? true : false;
  }

  populate() {
    if (this.multiple && this.value && (this.value as ProductService[]).length > 0) {
      const values = (this.value as ProductService[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false, display: true }));
    } else if (!this.multiple && this.value) {
      const value = (this.value as ProductService);
      this.selected = [{ ...value, selected: true, disabled: false, display: true }];
    }
  }

  selectedToDisplay() {
    return this.selected.filter((o) => o.display);
  }

  async setInitialState() {
    await this.retrieveProductsAndServices(true);
    if (this.multiple && this.value && (this.value as ProductService[]).length > 0) {
      const values = (this.value as ProductService[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false }));
      this.productsServices.docs.forEach(ps => {
        const found = values.find(t => t._id === ps._id);
        if (found) {
          ps.selected = true;
        } else {
          ps.selected = false;
        }
      });
      const value = _.unionWith([...this.productsServices.docs.filter(c => c.selected), ...this.selected], this._productServiceComparator);
      if (value.length > 0) {
        this.selection = value.length === 1 ? (this.truncate(value[0].label, 25)) : `${this.truncate(this.label)} (${value.length})`;
      } else {
        this.selection = undefined;
      }
    } else if (!this.multiple && this.value) {
      const value = (this.value as ProductService);
      this.selected = [{ ...value, selected: true, disabled: false }];
      const option = this.productsServices.docs.find(ps => ps._id === value._id);
      if (option) {
        option.selected = true;
      }
      this.selection = value ? (this.truncate(value.label)) : undefined;
    }
    if (this.emitOnInit) {
      this.onChange();
    }

  }

  removeFromPreselection(option: any) {
    this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    const doc = this.productsServices.docs.find((ps) => ps._id === option._id);
    if (doc) {
      doc.selected = false;
    }
    this.onChange()
  }

  toggleFromSelection(option: ProductServiceOption) {
    if (!option.selected) {
      this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    } else if (!this._isSelected(option)) {
      this.selected.push({ ...option, display: false });
    }
    this.selected.sort((a, b) => (a.label || 'a').localeCompare(b.label || 'a'))
  }

  resetPage() {
    this.productsServices.limit = 10;
    this.productsServices.page = 1;
    this.productsServices.offset = 0;
  }

  _productServiceComparator(productServiceA: ProductService, productServiceB: ProductService) {
    return productServiceA._id === productServiceB._id;
  }

  async retrieveProductsAndServices(replace: boolean = false) {
    this.loading = true;
    const result = await this.productServiceService.retrieveAll(this.productsServices.page, this.productsServices.limit, this.productsServices.offset, 'label', [], this.niceClassification, this.trademarks, this.query)
    this.productsServices = {
      ...result,
      docs: replace ? [...result.docs.map(ps => ({ ...ps, disabled: this._isDisabled(ps), selected: this._isSelected(ps) }))] : _.unionWith([...this.productsServices.docs, ...result.docs.map(ps => ({ ...ps, disabled: this._isDisabled(ps), selected: this._isSelected(ps) }))], this._productServiceComparator)
    };
    this.loading = false;
  }

  select(option: any) {
    option.selected = option.selected !== null ? !option.selected : true;
    this.toggleFromSelection(option)
    if (!this.multiple) {
      this.productsServices.docs.forEach(t => {
        if (t._id !== option._id) {
          t.selected = false;
        }
      })
    }
    this.onChange();
  }

  // TODO: handle trademarks having countrycode which is a wipo code (ex: EM)
  onChange() {
    if (!this.multiple) {
      const value = this.productsServices.docs.find(c => c.selected) !== undefined ? this.productsServices.docs.find(c => c.selected) : (this.selected.length === 1 ? this.selected[0] : undefined);
      this.selection = value ? `${this.truncate(this.label)} (1)` : undefined;
      this.valueChange.emit(value);
      this.change.emit({ value });
      this.trigger?.close();
    } else {
      const values = _.unionWith([...this.productsServices.docs.filter(c => c.selected), ...this.selected], this._productServiceComparator);
      if (values.length > 0) {
        this.selection = `${this.truncate(this.label)} (${values.length})`;
      } else {
        this.selection = undefined;
      }
      this.valueChange.emit(values);
      this.change.emit({ value: values });
    }
  }


  toggle() {
    this.show = !this.show;
    if (this.show) {
      this._message.emitChange("LAYOUT", "SELECT", {
        id: this.id
      });
    }
  }

  filtered() {
    return this.productsServices.docs;
  }

  onClear() {
    if (this.multiple) {
      this.value = [];
    } else {
      this.value = undefined;
    }
    this.productsServices.docs.forEach(ps => {
      ps.selected = false;
    });
    this.selected = [];
    this.selection = undefined;
    this.query = undefined;
    this.onChange();
    this.trigger?.close();
  }

  onQueryChange(event: any) {
    this.queryUpdate.next(this.query);
  }


  onScroll() {

    this.productsServices.offset = this.productsServices.page * this.productsServices.limit;
    this.productsServices.page = this.productsServices.page + 1;
    this.retrieveProductsAndServices();
  }

  truncate(value?: string, number?: number) {
    if (!value || value.length < (number || 20)) return value;
    return `${value.substring(0, (number || 20))}...`
  }

  getProductServiceLabel(productService: ProductService) {
    return this.productServiceService.getLabel(productService, this.user?.lang);
  }

  async removeTrademark(index: number) {
    this.trademarks.splice(index, 1);
    this.resetPage();
    await this.retrieveProductsAndServices(true);
  }

  async removeNice(index: number) {
    this.niceClassification.splice(index, 1);
    this.resetPage();
    await this.retrieveProductsAndServices(true);
  }
}
